import React from 'react'

import { useJointScenarioAndAssociationsQuery } from '@api/jointScenarios/queries/useJointScenarioAndAssociationsQuery'
import { useNoteTemplatesQuery } from '@api/scenarios/queries/useNoteTemplatesQuery'
import { App } from '@containers/App'
import { Tab, Tabs, TabList, TabPanel } from '@components/Tabs'
import useTimeValue from '@hooks/useTimeValue'
import useFeatureConfig, { FeatureNames } from '@hooks/useFeatureConfig'

import { ActionablesProvider } from '@contexts/ActionablesContext'

import { Grid, GridArea } from '@UILibrary/Grid'
import { ErrorBoundary } from '@components/ErrorBoundary'
import { LoadingStatusIndicator } from '@components/LoadingStatusIndicator'
import { ScenarioHeader } from '@components/ScenarioHeader'
import { CalculationConfig } from '@components/CalculationConfig'

import { Notes } from '@containers/Notes'

import { JointAssets } from './components/JointAssets'
import { JointCashflowEvents } from './components/JointCashflowEvents'
import { JointLiabilities } from './components/JointLiabilities'
import { JointBudget } from './components/JointBudget'
import { JointRisk } from './components/JointRisk'
import { JointRiskProducts } from './components/JointRiskProducts'
import { JointFna } from './components/JointFna'
import { JointMonthlyRetirementBudget } from './components/JointMonthlyRetirementBudget'
import { JointAssetAllocations } from './components/JointAssetAllocations'
import { JointScenarioActions } from './components/JointScenarioActions'
import { JointForecast } from './components/JointForecast'
import { JointSafeRetirementIndicator } from './components/JointSafeRetirementIndicator'
import { JointClientDetailsCard } from './components/JointClientDetailsCard'

import routes from '@utils/routes'

import styles from "./JointScenario.module.scss"

export const JointScenario = (jointScenarioData) => {
  return (
    <App
      config={jointScenarioData.config}
    >
      <ActionablesProvider
        location='jointScenario'
      >
        <ErrorBoundary context={{
          tenant: jointScenarioData.config.tenant,
          userDetails: jointScenarioData.config.userDetails
        }}>
          <JointScenarioComponents {...jointScenarioData} />
        </ErrorBoundary>
      </ActionablesProvider>
    </App>
  )
}

const jointScenarioName = (participants) => {
  return `${participants[0].preferredName} & ${participants[1].preferredName}'s Joint Plan`
}

const JointScenarioComponents = (jointScenarioData) => {
  const { isEnabled: isAssetEventsEnabled } = useFeatureConfig(FeatureNames.ASSET_EVENT__ALL)
  const {
    participantsQueries,
    notes,
    forecast,
    jointBudget,
    jointCashflowEvents,
    assetAllocation,
    isFetching,
    loadingIndicators
  } = useJointScenarioAndAssociationsQuery({
    tenantSlug: jointScenarioData.config.tenant.slug,
    jointScenarioId: jointScenarioData.jointScenario.id,
    jointScenarioData: jointScenarioData
  })

  const noteTemplates = useNoteTemplatesQuery({
    scenarioID: jointScenarioData.jointScenario.id,
    tenantSlug: jointScenarioData.config.tenant.slug,
    defaultValues: []
  })

  const { timeValue, setTimeValue } = useTimeValue({
    tenantSlug: jointScenarioData.config.tenant.slug,
    defaultValue: jointScenarioData.config.timeValue,
    clientId: jointScenarioData.config.userDetails.client_id
  })
  const jointAssets = participantsQueries.reduce((acc, participantQuery) => {
    return [...acc, ...participantQuery.assets.data]
  }, [])

  return (
    <Grid className={styles.jointScenarioLayout}>
      <LoadingStatusIndicator
        isFetching={isFetching}
        loadingIndicators={loadingIndicators} />
      <GridArea name='forecast'>
        <ScenarioHeader
          label='Plan Name'
          scenarioName={jointScenarioName(jointScenarioData.participants)}
          timeValue={timeValue}
          setTimeValue={setTimeValue}
        >
          <ScenarioHeader.PlanName />
          <ScenarioHeader.TimeValueToggle />
        </ScenarioHeader>
        <JointForecast
          timeValue={timeValue}
          colors={jointScenarioData.config.colors}
          forecast={forecast.data}
          participantsQueries={participantsQueries}
        />
      </GridArea>

      <GridArea name='aside'>
        <JointScenarioActions
          usePresentValue={true}
          tenant={{
            tenantSlug: jointScenarioData.config.tenant.slug
          }}
          paths={{
            jointPlanPdfReportPath: routes.downloadJointPlanPDFPath({
              tenantSlug: jointScenarioData.config.tenant.slug,
              clientId: jointScenarioData.config.userDetails.client_id,
            }, { usePresentValue: true }),
            jointScenarioPath: routes.jointScenarioPath({
              tenantSlug: jointScenarioData.config.tenant.slug,
              clientId: jointScenarioData.config.userDetails.client_id,
              jointScenarioId: jointScenarioData.jointScenario.id
            })
          }}
          clients={jointScenarioData.participants.map((participant) => {
            return {
              id: participant.id,
              preferredName: participant.preferredName
            }
          })}
        />
        <JointSafeRetirementIndicator
          timeValue={timeValue}
          participantsQueries={participantsQueries}
        />
        <JointClientDetailsCard
          participantsQueries={participantsQueries}
          forecast={forecast}
        />
        {isAssetEventsEnabled()
          ? <CalculationConfig assets={jointAssets} />
          : null
        }
      </GridArea>

      <GridArea name='budget'>
        <JointMonthlyRetirementBudget
          participantsQueries={participantsQueries}
          jointBudget={jointBudget.data}
        />
      </GridArea>

      <GridArea name='allocations' className='hidden lg:block'>
        <JointAssetAllocations
          participantsQueries={participantsQueries}
          jointAssetAllocation={assetAllocation.data}
        />
      </GridArea>

      <GridArea name='tabs'>
        <Tabs defaultIndex={0} forceRenderTabPanel className='card-tabs'>
          <TabList className='card-tabs__list overflow-x-scroll lg:overflow-auto'>
            <Tab
              className="card-tabs__list-item"
              selectedClassName="card-tabs__list-item--selected">
              <h2>Assets</h2>
            </Tab>
            <Tab
              className="card-tabs__list-item"
              selectedClassName="card-tabs__list-item--selected">
              <h2>Cashflow</h2>
            </Tab>
            <Tab
              className="card-tabs__list-item"
              selectedClassName="card-tabs__list-item--selected">
              <h2>Budget</h2>
            </Tab>
            <Tab
              className="card-tabs__list-item"
              selectedClassName="card-tabs__list-item--selected">
              <h2>Liabilities</h2>
            </Tab>
            <Tab
              className="card-tabs__list-item"
              selectedClassName="card-tabs__list-item--selected">
              <h2>Risk profile</h2>
            </Tab>
            <Tab
              className="card-tabs__list-item"
              selectedClassName="card-tabs__list-item--selected">
              <h2>Risk products</h2>
            </Tab>
            <Tab
              className="card-tabs__list-item"
              selectedClassName="card-tabs__list-item--selected">
              <h2>Notes</h2>
            </Tab>
            <Tab
              className="card-tabs__list-item"
              selectedClassName="card-tabs__list-item--selected">
              <h2>FNA</h2>
            </Tab>
          </TabList>
          <TabPanel>
            <JointAssets
              participantsQueries={participantsQueries}
              config={jointScenarioData.config}
            />
          </TabPanel>
          <TabPanel>
            <JointCashflowEvents
              jointCashflowEvents={jointCashflowEvents.data}
              jointCrudActions={jointCashflowEvents.actions}
              participantsQueries={participantsQueries}
              config={jointScenarioData.config}
            />
          </TabPanel>
          <TabPanel>
            <JointBudget
              jointBudget={jointBudget.data}
              crudActions={jointBudget.actions}
              participantsQueries={participantsQueries}
              config={jointScenarioData.config}
              jointForecast={forecast.data}
            />
          </TabPanel>
          <TabPanel>
            <JointLiabilities
              participantsQueries={participantsQueries}
              config={jointScenarioData.config}
            />
          </TabPanel>
          <TabPanel>
            <JointRisk
              participantsQueries={participantsQueries}
              config={jointScenarioData.config}
            />
          </TabPanel>
          <TabPanel>
            <JointRiskProducts
              participantsQueries={participantsQueries}
              config={jointScenarioData.config}
            />
          </TabPanel>
          <TabPanel>
            <Notes
              notes={notes.data}
              noteTemplates={noteTemplates.data}
              withTemplates={jointScenarioData.config.featureToggles.noteTemplates}
              crudActions={notes.actions}
            />
          </TabPanel>
          <TabPanel>
            <JointFna
              participantsQueries={participantsQueries}
              config={jointScenarioData.config}
            />
          </TabPanel>
        </Tabs>
      </GridArea>
    </Grid>
  )
}
